import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { tminFormField } from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoAnnularRingJSON {
  type: GeckoFeatureModelType.AnnularRing
  name: string
  feature_geometry: {
    inner_radius: number // Inner radius (start of ring)
  }
  tmin: number | undefined
  nominal: {
    base_thickness: number | undefined
  }
}

export const NewGeckoAnnularRingJSON: GeckoAnnularRingJSON = {
  type: GeckoFeatureModelType.AnnularRing,
  name: 'New Annular Ring',
  feature_geometry: {
    inner_radius: 0,
  },
  nominal: {
    base_thickness: undefined,
  },
  tmin: undefined,
}

export class GeckoAnnularRing extends GeckoEditableFeature {
  public readonly name: string
  public readonly type = GeckoFeatureModelType.AnnularRing
  public readonly innerRadius: GeckoMeasurement
  public readonly outerRadius: GeckoMeasurement
  public readonly baseNominalThickness: GeckoMeasurement | undefined
  public readonly tmin: GeckoMeasurement | undefined

  private constructor(
    id: string,
    name: string,
    innerRadius: GeckoMeasurement,
    outerRadius: GeckoMeasurement,
    baseNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined
  ) {
    super(id)
    this.name = name
    this.innerRadius = innerRadius
    this.outerRadius = outerRadius
    this.baseNominalThickness = baseNominalThickness
    this.tmin = tmin
  }

  public static CreateArrayFromJSON(
    componentID: string,
    annularRingsJSON: Record<string, GeckoAnnularRingJSON>,
    tankFloorRadius: GeckoMeasurement
  ): ReadonlyArray<GeckoAnnularRing> | undefined {
    const entries = Object.entries(annularRingsJSON)
    if (entries.length === 0) return undefined

    try {
      const basicRingData: {
        id: string
        name: string
        innerRadius: number
        baseNominalThickness?: number
        tmin?: number
      }[] = entries
        .map(([ringID, ringJSON]) => {
          const name = parseString(ringJSON.name) ?? 'Annular Ring'
          const innerRadius =
            parseNumber(ringJSON.feature_geometry?.inner_radius) ?? 0
          const baseNominalThickness = parseNumber(
            ringJSON.nominal?.base_thickness
          )
          const tmin = parseNumber(ringJSON.tmin)

          return {
            id: ringID,
            name,
            innerRadius,
            baseNominalThickness: baseNominalThickness ?? undefined,
            tmin: tmin ?? undefined,
          }
        })
        .toSorted((a, b) => a.innerRadius - b.innerRadius)

      return basicRingData.map((brd, index, arr) => {
        const nextInnerRadius =
          index < arr.length - 1 ? arr[index + 1].innerRadius : undefined
        const outerRadiusMeters =
          nextInnerRadius !== undefined
            ? nextInnerRadius
            : tankFloorRadius.convertTo(GeckoMeasurementUnit.Meters).num

        const inner = GeckoMeasurement.CreateFromValue(
          brd.innerRadius,
          GeckoMeasurementUnit.Meters
        )
        const outer = GeckoMeasurement.CreateFromValue(
          outerRadiusMeters,
          GeckoMeasurementUnit.Meters
        )

        const baseNominalThickness =
          brd.baseNominalThickness === undefined
            ? undefined
            : weightThicknessValue(
                GeckoMeasurement.CreateFromValue(
                  brd.baseNominalThickness,
                  GeckoMeasurementUnit.Meters
                )
              )

        const tmin =
          brd.tmin === undefined
            ? undefined
            : weightThicknessValue(
                GeckoMeasurement.CreateFromValue(
                  brd.tmin,
                  GeckoMeasurementUnit.Meters
                )
              )

        return new GeckoAnnularRing(
          brd.id,
          brd.name,
          inner,
          outer,
          baseNominalThickness,
          tmin
        )
      })
    } catch (e) {
      console.error(e)
      return undefined
    }
  }

  public static CreateMapFromJSON(
    componentID: string,
    annularRingsJSON: Record<string, GeckoAnnularRingJSON>,
    tankFloorRadius: GeckoMeasurement
  ): Map<string, GeckoAnnularRing> {
    const array = this.CreateArrayFromJSON(
      componentID,
      annularRingsJSON,
      tankFloorRadius
    )
    const result = new Map<string, GeckoAnnularRing>()
    array?.forEach((ring) => {
      result.set(ring.id, ring)
    })
    return result
  }

  public static CreateFromJSON(
    componentID: string,
    ringID: string,
    ringJSON: GeckoAnnularRingJSON,
    tankFloorRadius: GeckoMeasurement
  ): GeckoAnnularRing {
    const name = parseString(ringJSON.name) ?? 'Annular Ring'

    const innerRadius = GeckoMeasurement.CreateFromValue(
      ringJSON.feature_geometry.inner_radius,
      GeckoMeasurementUnit.Meters
    )

    // Fallback: when creating a single ring independently, use the floor radius as outer radius
    const outerRadius = tankFloorRadius

    const parsedBaseThickness = parseNumber(ringJSON.nominal?.base_thickness)
    const baseNominalThickness =
      parsedBaseThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedBaseThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const parsedTmin = parseNumber(ringJSON.tmin)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    return new GeckoAnnularRing(
      ringID,
      name,
      innerRadius,
      outerRadius,
      baseNominalThickness,
      tmin
    )
  }

  public asJSON(): GeckoAnnularRingJSON {
    return generateAnnularRing(
      this.name,
      this.innerRadius,
      this.baseNominalThickness,
      this.tmin
    )
  }

  public getTableOptions(): FeatureTableOptions {
    return {
      type: GeckoFeatureModelType.AnnularRing,
      title: 'Annular Rings',
      singularName: 'Annular Ring',
      fields: [
        'name',
        'innerRadius',
        'outerRadius',
        'baseNominalThickness',
        'tmin',
      ],
    }
  }
}

export function generateAnnularRing(
  name: string,
  innerRadius: GeckoMeasurement,
  baseNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined
): GeckoAnnularRingJSON {
  const ringJSON: GeckoAnnularRingJSON = {
    type: GeckoFeatureModelType.AnnularRing,
    name: name,
    feature_geometry: {
      inner_radius: innerRadius
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
    nominal: {
      base_thickness: undefined,
    },
    tmin: undefined,
  }

  if (baseNominalThickness !== undefined) {
    ringJSON.nominal.base_thickness = baseNominalThickness
      .convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  }

  if (tmin !== undefined) {
    ringJSON.tmin = tmin
      .convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  }

  return ringJSON
}

export const AnnularRingFormFields = (
  annularRingJSON: GeckoAnnularRingJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Ring Name',
      value: annularRingJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'inner_radius',
      label: 'Inner Radius',
      value: annularRingJSON.feature_geometry.inner_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'base_thickness',
      label: 'Nominal Thickness',
      value: annularRingJSON.nominal.base_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    tminFormField(annularRingJSON.tmin),
  ]
}

export const AnnularRingJSONFromFormFields = (formFields: FormField[]) => {
  const name = getFormFieldByDataKey(formFields, 'name')?.value ?? ''
  const innerRadius = getFormFieldByDataKey(formFields, 'inner_radius')
  const baseNominalThickness = getFormFieldByDataKey(
    formFields,
    'base_thickness'
  )
  const tmin = getFormFieldByDataKey(formFields, 'tmin')

  return generateAnnularRing(
    name as string,
    GeckoMeasurement.CreateFromValue(
      innerRadius?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    baseNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          baseNominalThickness.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    tmin?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          tmin.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined
  )
}

export const AnnularRingFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.AnnularRing,
  title: 'Annular Rings',
  singularName: 'Annular Ring',
  fields: ['name', 'inner_radius', 'outer_radius', 'base_thickness', 'tmin'],
}
